import PitchData from '../../enums/PitchData.js';
import HitData from '../../enums/HitData.js';
import BaseballPitchType from '../../enums/BaseballPitchType.js';
import PitchMechanics from '../../enums/PitchMechanics.js';
import Handedness from '../../enums/Handedness.js';
import BattedBallType from '../../enums/BattedBallType.js';
import TaggingData from '../../enums/TaggingData.js';
import MlbPitchTypeMapper from '../../mappers/mlbEnumsMappers/MlbPitchTypeMapper.js';
import MlbBattedBallTypeMapper from '../../mappers/mlbEnumsMappers/MlbBattedBallTypeMapper.js';
import MapperBase from '../../mappers/MapperBase.js';

class MlbTaggingDataConverter {
    constructor(pitchTypeMapper, battedBallTypeMapper) {
        if (typeof(pitchTypeMapper) !== 'undefined' && !(pitchTypeMapper instanceof MapperBase)) {
            throw new TypeError('pitchTypeMapper must be undefined (to use default implementation) or MapperBase\'s class instance');
        }
        if (typeof(battedBallTypeMapper) !== 'undefined' && !(battedBallTypeMapper instanceof MapperBase)) {
            throw new TypeError('battedBallTypeMapper must be undefined (to use default implementation) or MapperBase\'s class instance');
        }
        this.pitchTypeMapper = pitchTypeMapper || new MlbPitchTypeMapper();
        this.battedBallTypeMapper = battedBallTypeMapper || new MlbBattedBallTypeMapper();

        this.tagginPitch = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/data/result-tagging-pitch-v2.json
            [PitchData.Classification.key]: BaseballPitchType.Unidentified.key,
            [PitchData.Mechanics.key]: PitchMechanics.Unidentified.key,
            [PitchData.StrikeZonePosition.key]: 0,
            [PitchData.Handedness.key]: Handedness.Unidentified.key,
            [PitchData.SwingHandedness.key]: Handedness.Unidentified.key,
            [TaggingData.BattedBallType.key]: BattedBallType.Unidentified.key,
            [TaggingData.IsBunt.key]: null,
            [TaggingData.BallInDirt.key]: null,
        };

        this.tagginHit = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/data/result-tagging-hit-v2.json
            [PitchData.Handedness.key]: Handedness.Unidentified.key, // TODO - maybe we need it in hit as well ? this is marked as common in DTO, no issues if name of property stays the same
            [HitData.SwingHandedness.key]: Handedness.Unidentified.key,
            [TaggingData.BattedBallType.key]: BattedBallType.Unidentified.key,
            [TaggingData.IsBunt.key]: null,
            [TaggingData.BallInDirt.key]: null,
        };
    }

    /**
     * Returns object with pitch and hit property from a single play event
     * @param {Object} playEvent - item from liveData.plays.allPlays[].playEvents, Note, only playEvents of type "ptich" are supported
     */
    convert(play, playEvent) {
        const taggingData = {};

        if (playEvent.pitchData) {
            taggingData.pitch = this.convertPitchData(playEvent, play.matchup);
        }

        if (playEvent.hitData) {
            taggingData.hit = this.convertHitData(playEvent, play.matchup);
        }

        return taggingData;
    }

    convertPitchData(playEvent, matchup) {
        const pitchTagging = Object.assign({}, this.tagginPitch);
        this.readCommonTaggingData(playEvent, matchup, pitchTagging);
        if (playEvent.details && playEvent.details.type) {
            pitchTagging[PitchData.Classification.key] = this.pitchTypeMapper.map(playEvent.details.type.code);
        }
        if (playEvent.pitchData && typeof(playEvent.pitchData.zone) !== 'undefined') {
            pitchTagging[PitchData.StrikeZonePosition.key] = playEvent.pitchData.zone;

            // TODO - PitchData.Mechanics... probably based on runner on base status
            // The windup is used when there are no runners on base or there is only a runner on third. @see https://www.ducksters.com/sports/baseball/pitching_stretch_and_windup.php
        }

        return pitchTagging;
    }

    convertHitData(playEvent, matchup) {
        const hitTagging = Object.assign({}, this.tagginHit);
        this.readCommonTaggingData(playEvent, matchup, hitTagging);

        return hitTagging;
    }

    readCommonTaggingData(playEvent, matchup, tagginData) {
        if (matchup.pitchHand && matchup.pitchHand.code) {
            // TODO  mapper ?
            tagginData[PitchData.Handedness.key] = matchup.pitchHand.code;
        }
        if (matchup.batSide && matchup.batSide.code) {
            // TODO  mapper ?
            tagginData[PitchData.SwingHandedness.key] = matchup.batSide.code;
        }

        if (playEvent.hitData) {
            tagginData[TaggingData.BattedBallType.key] = this.battedBallTypeMapper.map(playEvent.hitData.trajectory);
        }

        if (playEvent.details && playEvent.details.description) {
            tagginData[TaggingData.BallInDirt.key] = (
                // TODO - maybe use code ?
                playEvent.details.description === 'Ball In Dirt'
                || playEvent.details.description === 'Swinging Strike (Blocked)'
            );

            // TODO - this is far from perfect
            tagginData[TaggingData.IsBunt.key] = playEvent.details.description.toLowerCase().indexOf('bunt') > -1;
        }
    }
}

export default MlbTaggingDataConverter;